<template>
  <div>
    <!-- Empty state -->
    <div v-if="!practice" class="bg-growth-10 rounded-lg border border-growth-50 p-12 text-center text-growth-600">
      <svg class="w-16 h-16 mx-auto mb-4 text-growth-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
      Select a practice from the list to view details
    </div>

    <!-- Practice card -->
    <div v-else class="bg-white rounded-lg border border-growth-50 overflow-hidden shadow-growth-sm">
      <!-- Header: name, IDs, tags -->
      <div class="p-5 pb-0">
        <div class="flex justify-between items-start">
          <div>
            <h2 class="sensitive text-xl font-semibold text-growth-900">{{ practice.NAME }}</h2>
            <div class="text-sm text-growth-600 flex items-center gap-3 mt-1">
              <span>PAC: {{ practice.PAC_ID }}</span>
              <span v-if="practice.TIN_ID">TIN: {{ practice.TIN_ID }}</span>
            </div>
            <div v-if="practice.CITY" class="text-sm text-growth-700 mt-0.5">{{ practice.CITY }}, {{ practice.STATE }}</div>
          </div>
          <button v-if="practice.TIN_ID" @click="$emit('toggle-aco-builder', practice)" :class="['px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200', inAcoBuilder ? 'bg-emerald-100 text-emerald-700' : 'bg-growth-10 text-growth-600 hover:bg-growth-25']">
            {{ inAcoBuilder ? '✓ In ACO Builder' : '+ Add to ACO Builder' }}
          </button>
        </div>
        <div class="flex flex-wrap gap-1.5 mt-3">
          <!-- ACO Program -->
          <PracticeTag v-if="practice.IN_MSSP" :title="practice.MSSP_ACO_NAME" category="aco" filled class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'aco', value: 'mssp'})">MSSP</PracticeTag>
          <PracticeTag v-else-if="practice.IN_REACH" :title="practice.REACH_ACO_NAME" category="aco" filled class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'aco', value: 'reach'})">REACH</PracticeTag>
          <PracticeTag v-else-if="practice.PRIOR_MSSP" category="volume" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'aco', value: 'prior_mssp'})">Prior MSSP</PracticeTag>
          <PracticeTag v-else-if="practice.PRIOR_REACH" category="population" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'aco', value: 'prior_reach'})">Prior REACH</PracticeTag>
          <!-- ACO Performance -->
          <PracticeTag v-if="practice.ACO_SAVINGS_TIER" :variant="acoPerformanceVariant" :title="acoPerformanceTooltip" category="aco" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'acoSavings', value: acoPerformanceFilterValue})">{{ practice.ACO_SAVINGS_TIER }}</PracticeTag>
          <!-- Practice type -->
          <PracticeTag v-if="practice.PCP_HEAVY" category="type">PCP</PracticeTag>
          <PracticeTag v-if="practice.PRACTICE_SIZE === 'solo'" category="type" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'practiceSize', value: 'solo'})">Solo</PracticeTag>
          <PracticeTag v-else-if="practice.PRACTICE_SIZE === 'large'" category="type" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'practiceSize', value: 'large'})">Large</PracticeTag>
          <PracticeTag v-if="practice.SERVICE_MIX === 'Primary Care'" category="type" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'serviceMix', value: 'primary'})">Primary Care</PracticeTag>
          <PracticeTag v-else-if="practice.SERVICE_MIX === 'Procedural'" category="volume" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'serviceMix', value: 'procedural'})">Procedural</PracticeTag>
          <!-- Volume/trend -->
          <PracticeTag v-if="practice.VOLUME_TIER" category="volume" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'volume', value: practice.VOLUME_TIER})">{{ practice.VOLUME_TIER }}</PracticeTag>
          <PracticeTag v-if="practice.PAYMENT_TREND === 'Growing'" category="volume" variant="success" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'trend', value: 'growing'})">Growing</PracticeTag>
          <PracticeTag v-else-if="practice.PAYMENT_TREND === 'Declining'" category="volume" variant="warning" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'trend', value: 'declining'})">Declining</PracticeTag>
          <!-- Setting -->
          <PracticeTag v-if="practice.SETTING_PREFERENCE === 'Office-Based'" category="setting" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'setting', value: 'office'})">Office</PracticeTag>
          <PracticeTag v-if="practice.LOCATION_TYPE === 'Rural'" category="setting" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'location', value: 'rural'})">Rural</PracticeTag>
          <PracticeTag v-if="practice.NURSING_FACILITY_INVOLVEMENT && practice.NURSING_FACILITY_INVOLVEMENT !== 'no_nursing_facility'" category="setting" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'careSettingType', value: 'snf'})">SNF</PracticeTag>
          <PracticeTag v-if="practice.INPATIENT_INVOLVEMENT === 'hospitalist'" category="setting" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'careSettingType', value: 'hospitalist'})">Hospitalist</PracticeTag>
          <PracticeTag v-if="practice.HOME_VISIT_INVOLVEMENT && practice.HOME_VISIT_INVOLVEMENT !== 'no_home_visits'" category="setting" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'careSettingType', value: 'home'})">Home</PracticeTag>
          <!-- Population -->
          <PracticeTag v-if="practice.PANEL_ACUITY === 'complex_acuity'" category="population" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'panelAcuity', value: 'complex_acuity'})">Complex</PracticeTag>
          <PracticeTag v-if="practice.DUAL_ELIGIBLE_LEVEL === 'dual_very_high'" category="population" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'dualLevel', value: 'dual_very_high'})">Safety Net</PracticeTag>
          <!-- Hospital -->
          <PracticeTag v-if="practice.HOSPITAL_AFFILIATED" :title="practice.PRIMARY_HOSPITAL_NAME" category="volume" variant="warning" class="cursor-pointer" @click.stop="$emit('filter-by-tag', {key: 'hospital', value: 'yes'})">Hospital</PracticeTag>
          <!-- Tech -->
          <PracticeTag v-if="practice.EHR_VENDOR" :title="practice.EHR_VENDOR" category="tech">{{ practice.EHR_VENDOR }}</PracticeTag>
        </div>
      </div>

      <!-- Tabs (directly under tags) -->
      <div class="flex border-b border-growth-50 mt-4 px-5">
        <button
          v-for="tab in tabs"
          :key="tab"
          @click="activeSubTab = tab"
          :class="['px-4 py-2.5 text-sm font-medium transition-colors relative',
            activeSubTab === tab
              ? 'text-growth-tangerine'
              : 'text-growth-600 hover:text-growth-900']"
        >
          {{ tab }}
          <div v-if="activeSubTab === tab" class="absolute bottom-0 left-0 right-0 h-0.5 bg-growth-tangerine"></div>
        </button>
      </div>

      <!-- Tab content -->
      <div class="p-4">
        <!-- Overview tab -->
        <template v-if="activeSubTab === 'Overview'">
          <div v-if="detailLoading" class="text-growth-600 p-8 text-center">Loading practice details...</div>
          <div v-else class="space-y-4">
            <!-- Summary metrics -->
            <div class="grid grid-cols-4 gap-3">
              <MetricCard :value="practice.ATTRIBUTED_BENES || '—'" label="Attributed Benes" :format="fmtNum" />
              <MetricCard :value="detail?.provider_count || '—'" label="Providers" :format="fmtNum" />
              <MetricCard :value="totalMedicare" label="Medicare Spending" :format="fmtCur" />
              <MetricCard :value="totalMedicaid" label="Medicaid Spending" :format="fmtCur" />
            </div>

            <PracticePaymentInsights :pac-id="practice.PAC_ID" />
            <PracticeHospitals :insights="insights" />
            <PracticeDrugPrescribing :insights="insights" />
          </div>
        </template>

        <!-- Providers tab -->
        <template v-else-if="activeSubTab === 'Providers'">
          <PracticeProviders :pac-id="practice.PAC_ID" />
        </template>

        <!-- ACO Participation tab -->
        <template v-else-if="activeSubTab === 'ACO'">
          <div class="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
            Shows the practice's primary ACO and its savings performance over time. Savings rate is the percentage of the benchmark saved; earned savings is the dollar amount returned to the ACO. Data from CMS MSSP and REACH public use files.
          </div>
          <PracticeAcoHistory :insights="insights" :loading="insightsLoading" />
        </template>

        <!-- Peer Benchmark tab -->
        <template v-else-if="activeSubTab === 'Peer Benchmark'">
          <div class="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
            Compares this practice's per-member-per-year cost against peers with similar characteristics. Toggle dimensions on/off to widen or narrow the peer group, then click Recompute. Peers are matched nationally by practice tags — not geography.
          </div>
          <PracticePeerBenchmark
            :data="peerBenchmark"
            :loading="peerLoading"
            @update-dimensions="refetchPeerBenchmark"
          />
        </template>

        <!-- Primary Care Model tab -->
        <template v-else-if="activeSubTab === 'Primary Care Model'">
          <div class="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
            Runs a 13-block financial model using real claims data from this practice. Each step shows where inputs came from: <span class="text-emerald-700 font-medium">provided</span> = real data, <span class="text-blue-700 font-medium">from:block</span> = derived from an earlier step, <span class="text-gray-500 font-medium">default</span> = industry assumption. More provided inputs = more accurate results.
          </div>
          <PracticeFinancial :data="financial" :loading="financialLoading" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import PracticeTag from '../PracticeTag.vue'
import MetricCard from './MetricCard.vue'
import PracticeProviders from '../modules/PracticeProviders.vue'
import PracticePaymentInsights from '../modules/PracticePaymentInsights.vue'
import PracticeHospitals from '../modules/PracticeHospitals.vue'
import PracticeAcoHistory from '../modules/PracticeAcoHistory.vue'
import PracticeDrugPrescribing from '../modules/PracticeDrugPrescribing.vue'
import PracticeFinancial from '../modules/PracticeFinancial.vue'
import PracticePeerBenchmark from '../modules/PracticePeerBenchmark.vue'

export default {
  name: 'PracticeCard',
  components: {
    PracticeTag,
    MetricCard,
    PracticeProviders,
    PracticePaymentInsights,
    PracticeHospitals,
    PracticeAcoHistory,
    PracticeDrugPrescribing,
    PracticeFinancial,
    PracticePeerBenchmark,
  },
  props: {
    practice: { type: Object, default: null },
    detail: { type: Object, default: null },
    detailLoading: { type: Boolean, default: false },
    insights: { type: Object, default: null },
    insightsLoading: { type: Boolean, default: false },
    financial: { type: Object, default: null },
    financialLoading: { type: Boolean, default: false },
    peerBenchmark: { type: Object, default: null },
    peerLoading: { type: Boolean, default: false },
    inAcoBuilder: { type: Boolean, default: false },
  },
  emits: ['toggle-aco-builder', 'refetch-peer-benchmark', 'tab-change', 'filter-by-tag'],
  data() {
    return {
      activeSubTab: 'Overview',
      tabs: ['Overview', 'Providers', 'ACO', 'Peer Benchmark', 'Primary Care Model'],
    }
  },
  watch: {
    activeSubTab(tab) {
      this.$emit('tab-change', tab)
    },
    practice() {
      this.activeSubTab = 'Overview'
    },
  },
  computed: {
    totalMedicare() {
      return this._sumProviderField('MEDICARE_PAYMENT')
    },
    totalMedicaid() {
      return this._sumProviderField('MEDICAID_PAYMENT')
    },
    acoPerformanceVariant() {
      const t = this.practice?.ACO_SAVINGS_TIER
      if (['Strong Saver', 'Moderate Saver'].includes(t)) return 'success'
      if (t === 'Losing') return 'warning'
      if (t === 'Volatile') return 'error'
      return null
    },
    acoPerformanceFilterValue() {
      const map = {
        'Strong Saver': 'strong', 'Moderate Saver': 'moderate',
        'Volatile': 'volatile', 'Losing': 'losing',
        'New to ACO': 'new', 'Never ACO': 'never',
      }
      return map[this.practice?.ACO_SAVINGS_TIER] || ''
    },
    acoPerformanceTooltip() {
      const p = this.practice
      if (!p) return ''
      const parts = []
      if (p.LATEST_SAVINGS_RATE != null) parts.push(`${p.LATEST_SAVINGS_RATE.toFixed(1)}% savings rate`)
      if (p.ACO_EARNED_SAVINGS) parts.push(`$${(p.ACO_EARNED_SAVINGS / 1e6).toFixed(1)}M earned`)
      if (p.ACO_SAVINGS_YEAR) parts.push(`PY${p.ACO_SAVINGS_YEAR}`)
      return parts.join(' · ')
    },
  },
  methods: {
    _sumProviderField(field) {
      if (!this.detail?.providers?.length) return null
      let sum = 0
      for (const p of this.detail.providers) {
        const v = p[field]
        if (v != null && isFinite(v)) sum += Number(v)
      }
      return sum || null
    },
    refetchPeerBenchmark(activeDimensions) {
      this.$emit('refetch-peer-benchmark', activeDimensions)
    },
    fmtNum(n) {
      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
      return n?.toLocaleString() || '0'
    },
    fmtCur(n) {
      if (n == null || !isFinite(n) || !n) return '—'
      if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return '$' + Math.round(n / 1000).toLocaleString() + 'K'
      return '$' + Math.round(n).toLocaleString()
    },
  },
}
</script>
